import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CaseManagementService, InboxCreate } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { toast } from "sonner";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { DATA_KEYS } from "@/utils/useRemoteData";

const INBOX_ORDER_KEY = "inbox-order-v1";

export const inboxOrderAtom = atomWithStorage<Record<string, number>>(
  INBOX_ORDER_KEY,
  {}
);

const EMPTY_ARRAY: any[] = [];

export const useInboxes = (enabled: boolean = true) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [inboxOrder, setInboxOrder] = useAtom(inboxOrderAtom);

  const {
    data: inboxes,
    isLoading,
    error,
  } = useQuery({
    queryKey: [DATA_KEYS.INBOXES, spotGroup?.id],
    queryFn: async () => {
      if (!spotGroup?.id) return null;
      const response = await CaseManagementService.listInboxes({
        path: { group_id: spotGroup.id },
        query: { page: 1, size: 100 },
      });
      return response.data?.items || [];
    },
    enabled: !!spotGroup?.id && enabled,
    staleTime: 5000,
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
  });

  const createMutation = useMutation({
    mutationFn: async (data: InboxCreate) => {
      if (!spotGroup?.id) throw new Error("No workspace selected");
      const response = await CaseManagementService.createInbox({
        path: { group_id: spotGroup.id },
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INBOXES, spotGroup?.id],
      });
      toast.success("Inbox created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create inbox: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      inboxId,
      data,
    }: {
      inboxId: string;
      data: Partial<InboxCreate>;
    }) => {
      if (!spotGroup?.id) throw new Error("No workspace selected");
      const response = await CaseManagementService.updateInbox({
        path: { group_id: spotGroup.id, inbox_id: inboxId },
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INBOXES, spotGroup?.id],
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update inbox: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (inboxId: string) => {
      if (!spotGroup?.id) throw new Error("No workspace selected");
      await CaseManagementService.deleteInbox({
        path: { group_id: spotGroup.id, inbox_id: inboxId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INBOXES, spotGroup?.id],
      });
      toast.success("Inbox deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete inbox: ${error.message}`);
    },
  });

  const updateOrder = (orderedInboxIds: string[]) => {
    const orderMap: Record<string, number> = {};
    orderedInboxIds.forEach((id, index) => {
      orderMap[id] = index;
    });
    setInboxOrder(orderMap);
  };

  return {
    inboxes: inboxes || EMPTY_ARRAY,
    isLoading,
    error,
    createInbox: createMutation.mutate,
    updateInbox: updateMutation.mutate,
    deleteInbox: deleteMutation.mutate,
    updateOrder,
    inboxOrder,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
